import { useState } from "react";
import { Crosshair, CalendarDays, User, Star, CheckCircle2, XCircle, ShieldCheck, MessageSquare, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { AccountStatusBadge, PriorityBadge, BuyingModeBadge } from "@/components/targeting/StatusBadge";
import {
  useUpdateAccount,
  useTeamReps,
  useFollowupGroups,
  useAccountFollowupGroups,
  useToggleAccountFollowupGroup,
} from "@/hooks/useTargetUniverse";
import { useAuth } from "@/contexts/AuthContext";
import { useSaveAccountNote } from "@/hooks/useAccountNotes";
import { AgentVerdictBadge } from "@/components/accounts/AgentVerdictBadge";

interface AccountTargetingCardProps {
  company: Record<string, any>;
}

function BoolIndicator({ value, label }: { value: boolean | null; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      {value ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
      )}
      <span className="text-xs">{label}</span>
    </div>
  );
}

export function AccountTargetingCard({ company }: AccountTargetingCardProps) {
  const updateAccount = useUpdateAccount();
  const { data: teamReps } = useTeamReps();
  const { data: followupGroups } = useFollowupGroups();
  const { data: accountGroups } = useAccountFollowupGroups(company.id);
  const toggleGroup = useToggleAccountFollowupGroup();
  const { userProfile } = useAuth();

  const performedBy = {
    id: userProfile?.id ?? null,
    name: userProfile?.sales_rep_name || userProfile?.full_name || "User",
  };

  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(company.targeting_status || "not_yet_reached");
  const [priority, setPriority] = useState(company.engagement_priority || "");
  const [assignedTo, setAssignedTo] = useState(company.targeting_assigned_to || "");
  const [followupDate, setFollowupDate] = useState(company.next_followup_date || "");
  const [tier, setTier] = useState(company.account_tier || "unreviewed");
  const [buyingMode, setBuyingMode] = useState(company.buying_mode || "");
  const [warmIntro, setWarmIntro] = useState(company.warm_intro_available || false);
  const [champion, setChampion] = useState(company.champion_identified || false);
  const [budgetConfirmed, setBudgetConfirmed] = useState(company.budget_confirmed || false);
  const [fitConfirmed, setFitConfirmed] = useState(company.fit_confirmed || false);
  const [qualReason, setQualReason] = useState(company.qualification_reason || "");

  const accountGroupIds = new Set((accountGroups || []).map((ag: any) => ag.group_id));

  const pipelineGateMet =
    buyingMode &&
    ["growth", "trouble", "overconfident"].includes(buyingMode) &&
    budgetConfirmed &&
    fitConfirmed;

  const handleSave = async () => {
    const oldStatus = company.targeting_status || "not_yet_reached";
    const oldTier = company.account_tier || "unreviewed";

    // Require qualification_reason when disqualifying
    if (tier === "disqualified" && !qualReason.trim()) {
      toast.error("Disqualification reason required", {
        description: "Please explain why this account should be disqualified before saving.",
      });
      return;
    }

    try {
      await updateAccount.mutateAsync({
        accountId: company.id,
        fields: {
          targeting_status: status,
          engagement_priority: priority || null,
          targeting_assigned_to: assignedTo || null,
          next_followup_date: followupDate || null,
          last_reviewed_at: new Date().toISOString(),
          last_reviewed_by: performedBy.name,
          account_tier: tier,
          buying_mode: buyingMode || null,
          warm_intro_available: warmIntro,
          champion_identified: champion,
          budget_confirmed: budgetConfirmed,
          fit_confirmed: fitConfirmed,
          qualification_reason: qualReason || null,
          ...(oldTier !== tier
            ? { tier_changed_at: new Date().toISOString(), tier_changed_by: performedBy.name }
            : {}),
        },
        activityType: oldTier !== tier ? "tier_changed" : oldStatus !== status ? "status_changed" : "reviewed",
        activityDescription:
          oldTier !== tier
            ? `Tier changed from ${oldTier} to ${tier}`
            : oldStatus !== status
              ? `Status changed from ${oldStatus} to ${status}`
              : "Account reviewed from detail page",
        activityMetadata: { old_status: oldStatus, new_status: status, old_tier: oldTier, new_tier: tier },
        performedBy,
      });
      toast.success("Targeting updated", { description: `${company.company_name} saved.` });
      setEditing(false);
    } catch {
      toast.error("Failed to update targeting");
    }
  };

  const handleCancel = () => {
    setStatus(company.targeting_status || "not_yet_reached");
    setPriority(company.engagement_priority || "");
    setAssignedTo(company.targeting_assigned_to || "");
    setFollowupDate(company.next_followup_date || "");
    setTier(company.account_tier || "unreviewed");
    setBuyingMode(company.buying_mode || "");
    setWarmIntro(company.warm_intro_available || false);
    setChampion(company.champion_identified || false);
    setBudgetConfirmed(company.budget_confirmed || false);
    setFitConfirmed(company.fit_confirmed || false);
    setQualReason(company.qualification_reason || "");
    setEditing(false);
  };

  const lastReviewed = company.last_reviewed_at
    ? new Date(company.last_reviewed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  // ─── READ-ONLY VIEW ───
  if (!editing) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Crosshair className="h-4 w-4" />
              Targeting
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Agent verdict badge (HITL) */}
          <AgentVerdictBadge companyId={company.id} />

          {/* Pipeline Gate Banner */}
          {company.buying_mode &&
            ["growth", "trouble", "overconfident"].includes(company.buying_mode) &&
            company.budget_confirmed &&
            company.fit_confirmed && (
              <div className="rounded-md bg-green-50 border border-green-200 p-2.5 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-green-700" />
                <p className="text-sm font-semibold text-green-700">Pipeline Gate Met — Qualifies for pipeline entry</p>
              </div>
            )}

          {/* Row 1: Core targeting fields */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Account Status</span>
              </div>
              <AccountStatusBadge status={company.account_tier} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Buying Mode</span>
              </div>
              <BuyingModeBadge mode={company.buying_mode} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Priority</span>
              </div>
              <PriorityBadge priority={company.engagement_priority} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Assigned To</span>
              </div>
              <p className="text-sm font-medium">{company.targeting_assigned_to || "—"}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Follow-up</span>
              </div>
              <p className="text-sm font-medium">
                {company.next_followup_date
                  ? new Date(company.next_followup_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  : "—"}
              </p>
            </div>
          </div>

          {/* Row 2: Qualification indicators */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <BoolIndicator value={company.budget_confirmed} label="Budget Confirmed" />
            <BoolIndicator value={company.fit_confirmed} label="Fit Confirmed" />
            <BoolIndicator value={company.warm_intro_available} label="Warm Intro" />
            <BoolIndicator value={company.champion_identified} label="Champion" />
          </div>

          {/* Follow-up Groups */}
          {(accountGroups || []).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Cadence:</span>
              {(followupGroups || [])
                .filter((g: any) => accountGroupIds.has(g.id))
                .map((g: any) => (
                  <span
                    key={g.id}
                    className="text-xs px-2 py-0.5 rounded-full border"
                    style={{ borderColor: g.color, color: g.color }}
                  >
                    {g.name}
                  </span>
                ))}
            </div>
          )}

          <Separator />

          {/* Notes (read-only view with inline new-note capture) */}
          <NotesSection company={company} userId={performedBy.id} />

          {lastReviewed && (
            <p className="text-xs text-muted-foreground">
              Last reviewed {lastReviewed}
              {company.last_reviewed_by ? ` by ${company.last_reviewed_by}` : ""}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // ─── EDIT VIEW ───
  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Crosshair className="h-4 w-4" />
          Edit Targeting
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {/* Agent verdict badge (HITL) — visible in edit mode too */}
          <AgentVerdictBadge companyId={company.id} />

          {/* Tier & Buying Intelligence */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tier & Buying Intelligence</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Account Status</Label>
                <Select value={tier} onValueChange={setTier}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unreviewed">Unreviewed</SelectItem>
                    <SelectItem value="cold">Cold</SelectItem>
                    <SelectItem value="validated_target">Validated Target</SelectItem>
                    <SelectItem value="nurture">Nurture</SelectItem>
                    <SelectItem value="pipeline_ready">Pipeline Ready</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="disqualified">Disqualified</SelectItem>
                    <SelectItem value="parked">Parked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Buying Mode</Label>
                <Select value={buyingMode} onValueChange={setBuyingMode}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Set mode" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="growth">Growth</SelectItem>
                    <SelectItem value="trouble">Trouble</SelectItem>
                    <SelectItem value="even_keel">Even Keel</SelectItem>
                    <SelectItem value="overconfident">Overconfident</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Set priority" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={budgetConfirmed} onCheckedChange={setBudgetConfirmed} id="ac-budget" />
                <Label htmlFor="ac-budget" className="text-xs">Budget Confirmed</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={fitConfirmed} onCheckedChange={setFitConfirmed} id="ac-fit" />
                <Label htmlFor="ac-fit" className="text-xs">Fit Confirmed</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={warmIntro} onCheckedChange={setWarmIntro} id="ac-warm" />
                <Label htmlFor="ac-warm" className="text-xs">Warm Intro</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={champion} onCheckedChange={setChampion} id="ac-champ" />
                <Label htmlFor="ac-champ" className="text-xs">Champion</Label>
              </div>
            </div>

            {pipelineGateMet && (
              <div className="rounded-md bg-green-50 border border-green-200 p-2.5 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-green-700" />
                <p className="text-sm font-semibold text-green-700">Pipeline Gate Met</p>
              </div>
            )}
          </section>

          <Separator />

          {/* Assignment & Follow-up */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Assignment & Follow-up</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Assigned To</Label>
                <Select value={assignedTo} onValueChange={setAssignedTo}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select rep" /></SelectTrigger>
                  <SelectContent>
                    {(teamReps || []).map((rep) => (
                      <SelectItem key={rep.first_name} value={rep.first_name}>{rep.first_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Follow-up Date</Label>
                <Input
                  type="date"
                  value={followupDate}
                  onChange={(e) => setFollowupDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Follow-up Cadence Groups */}
            <div>
              <Label className="text-xs mb-2 block">Follow-up Cadence</Label>
              <div className="flex flex-wrap gap-3">
                {(followupGroups || []).map((group: any) => (
                  <div key={group.id} className="flex items-center gap-1.5">
                    <Checkbox
                      checked={accountGroupIds.has(group.id)}
                      onCheckedChange={(checked) => {
                        toggleGroup.mutate({
                          accountId: company.id,
                          groupId: group.id,
                          action: checked ? "add" : "remove",
                          cadence: group.cadence,
                        });
                      }}
                    />
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: group.color }} />
                    <span className="text-xs">{group.name}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Adding a cadence auto-sets the next follow-up date.
              </p>
            </div>
          </section>

          {tier === "disqualified" && (
            <>
              <Separator />
              <section className="space-y-3">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Disqualification reason <span className="text-red-600">*</span>
                </h4>
                <Textarea
                  value={qualReason}
                  onChange={(e) => setQualReason(e.target.value)}
                  className={!qualReason.trim() ? "border-red-300 focus-visible:ring-red-400" : ""}
                  rows={2}
                  placeholder="Required. Why is this account disqualified? (e.g. 'Acquired by X, 2024', 'Not cell therapy — small molecules only')"
                />
                {!qualReason.trim() && (
                  <p className="text-[10px] text-red-600">Required when disqualifying an account.</p>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Free-form notes live in the Notes section of the read-only view — save tier first, then add notes there.
                </p>
              </section>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 mt-5">
          <Button size="sm" onClick={handleSave} disabled={updateAccount.isPending}>
            {updateAccount.isPending ? "Saving..." : "Save"}
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// NotesSection — always-visible block inside the read-only Targeting card.
// Shows the canonical rep note + author/timestamp, any legacy targeting_notes
// / qualification_reason when present, and an inline textarea with two actions:
//   - Save note            → writes companies.notes (+ stamps)
//   - Submit for review    → same write + flags note_review_status='pending_review'
//                            for the next rep_note_validator.py batch run.
// ───────────────────────────────────────────────────────────────────────────
function NotesSection({
  company,
  userId,
}: {
  company: Record<string, any>;
  userId: string | null;
}) {
  const saveNote = useSaveAccountNote();
  const [newNote, setNewNote] = useState("");

  const pending = company.note_review_status === "pending_review";
  const submittedAt = company.note_submitted_for_review_at
    ? new Date(company.note_submitted_for_review_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  // notes_updated_by is a uuid FK to user_profiles; resolve the display name
  // best-effort from the already-on-row fields so we don't need an extra round trip.
  const noteAuthor =
    company.last_reviewed_by ||
    company.sales_rep ||
    null;
  const noteUpdatedAt = company.notes_updated_at
    ? new Date(company.notes_updated_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const handleSave = async (submit: boolean) => {
    const trimmed = newNote.trim();
    if (!trimmed) {
      toast.error("Note is empty");
      return;
    }
    try {
      await saveNote.mutateAsync({
        companyId: company.id,
        note: trimmed,
        userId,
        submitForReview: submit,
      });
      setNewNote("");
      toast.success(
        submit
          ? "Note saved and submitted for review"
          : "Note saved",
        {
          description: submit
            ? "Agent will validate against the web on the next batch run."
            : undefined,
        }
      );
    } catch (e: any) {
      toast.error("Failed to save note", { description: e?.message });
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
          <MessageSquare className="h-3.5 w-3.5" />
          Notes
        </h4>
        {pending && (
          <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">
            <Clock className="h-3 w-3 mr-1" />
            Pending agent review
            {submittedAt ? ` · submitted ${submittedAt}` : ""}
          </Badge>
        )}
      </div>

      {/* Current note */}
      {company.notes ? (
        <div className="rounded-md border bg-muted/30 p-3">
          <p className="text-sm whitespace-pre-wrap">{company.notes}</p>
          {(noteAuthor || noteUpdatedAt) && (
            <p className="text-[11px] text-muted-foreground mt-2">
              {noteAuthor && <span>{noteAuthor}</span>}
              {noteAuthor && noteUpdatedAt && <span> · </span>}
              {noteUpdatedAt && <span>{noteUpdatedAt}</span>}
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">No notes yet.</p>
      )}

      {/* Legacy fields — rendered read-only when populated so nothing is lost */}
      {company.targeting_notes && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Targeting notes</div>
          <p className="text-sm whitespace-pre-wrap rounded-md border border-dashed bg-muted/20 p-2.5">
            {company.targeting_notes}
          </p>
        </div>
      )}
      {company.qualification_reason && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Qualification reason</div>
          <p className="text-sm whitespace-pre-wrap rounded-md border border-dashed bg-muted/20 p-2.5">
            {company.qualification_reason}
          </p>
        </div>
      )}

      {/* New-note input */}
      <div className="space-y-2">
        <Label className="text-xs">Add a note</Label>
        <Textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Capture a new note about this account. Submit for review to have it validated against the web."
          rows={3}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saveNote.isPending || !newNote.trim()}
          >
            Save note
          </Button>
          <Button
            size="sm"
            onClick={() => handleSave(true)}
            disabled={saveNote.isPending || !newNote.trim()}
          >
            {saveNote.isPending ? "Saving..." : "Submit for review"}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground">
          "Save note" writes to the account. "Submit for review" also queues it for the next agent validation run.
        </p>
      </div>
    </section>
  );
}
